import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Award,
  Calendar,
  CheckCircle2,
  Download,
  FileText,
  Gift,
  KeyRound,
  Loader2,
  ShieldCheck,
  Trash2,
  Upload,
  UploadCloud,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import PasswordModal from "@/components/auth/PasswordModal";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTIONS = ["new", "in_progress", "paid", "delivered", "cancelled"];
const STATUS_LABEL = {
  new: "New",
  in_progress: "In progress",
  paid: "Paid",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
const STATUS_TINT = {
  new: "bg-stone-100 text-stone-700",
  in_progress: "bg-amber-100 text-amber-800",
  paid: "bg-teal-100 text-teal-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-700",
};

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AdminClientDetail() {
  const { id } = useParams();
  const { user, loading, authHeader, API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const fileInputRef = useRef(null);

  const fetchClient = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/clients/${id}`, {
        headers: authHeader(),
        withCredentials: true,
      });
      setData(d);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 404) setError("Client not found.");
      else if (e?.response?.status === 403) setError("Admin access required.");
      else setError("Could not load client.");
    }
  }, [API, authHeader, id]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    if (!user.is_admin) {
      setError("Admin access required.");
      return;
    }
    fetchClient();
  }, [user, loading, navigate, fetchClient]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const { data: resp } = await axios.patch(
        `${API}/admin/leads/${leadId}`,
        { status: newStatus },
        { headers: authHeader(), withCredentials: true }
      );
      if (resp.reward?.awarded) {
        if (resp.reward.credits_created > 0) {
          toast.success(
            `+${resp.reward.awarded} pts — 🎁 ${resp.reward.credits_created} free credit unlocked!`
          );
        } else {
          toast.success(`+${resp.reward.awarded} pts awarded`);
        }
      } else {
        toast.message(`Status set to ${STATUS_LABEL[newStatus] || newStatus}`);
      }
      await fetchClient();
    } catch (e) {
      console.error(e);
      toast.error("Could not update status");
    }
  };

  const redeemCredit = async () => {
    try {
      await axios.post(
        `${API}/admin/clients/${id}/redeem-credit`,
        {},
        { headers: authHeader(), withCredentials: true }
      );
      toast.success("Free credit redeemed");
      await fetchClient();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not redeem credit");
    }
  };

  const handleFiles = async (filesList) => {
    if (!filesList || filesList.length === 0) return;
    setUploading(true);
    const note = noteText.trim() || null;
    try {
      for (const file of Array.from(filesList)) {
        if (file.size > 8_000_000) {
          toast.error(`${file.name} is too large (max 8 MB)`);
          continue;
        }
        const dataUrl = await readAsDataURL(file);
        await axios.post(
          `${API}/admin/clients/${id}/deliverables`,
          { filename: file.name, data: dataUrl, note },
          { headers: authHeader(), withCredentials: true }
        );
      }
      toast.success("Uploaded ✓");
      setNoteText("");
      await fetchClient();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteDeliverable = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API}/admin/clients/${id}/deliverables/${fileId}`, {
        headers: authHeader(),
        withCredentials: true,
      });
      toast.message("File removed");
      await fetchClient();
    } catch (e) {
      console.error(e);
      toast.error("Could not delete");
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const { data: doc } = await axios.get(
        `${API}/admin/clients/${id}/deliverables/${fileId}`,
        { headers: authHeader(), withCredentials: true }
      );
      const a = document.createElement("a");
      a.href = doc.data;
      a.download = doc.filename || filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    }
  };

  if (loading || (!data && !error)) {
    return (
      <main className="min-h-screen bg-[#FAF7F2]" data-testid="admin-client-loading">
        <Nav />
        <div className="pt-40 flex items-center justify-center text-stone-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="min-h-screen bg-[#FAF7F2]">
        <Nav />
        <div className="pt-40 px-6 lg:px-12 max-w-2xl mx-auto">
          <div className="bg-white border border-stone-200 rounded-[2rem] p-10 text-center">
            <div className="inline-flex h-12 w-12 rounded-full bg-rose-100 items-center justify-center mb-4">
              <ShieldCheck className="h-5 w-5 text-rose-700" />
            </div>
            <h1 className="font-display text-3xl font-semibold">{error}</h1>
            <Link
              to="/admin/clients"
              className="mt-6 inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to clients
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const u = data.user;
  const leads = data.leads || [];
  const deliverables = data.deliverables || [];
  const pointsLog = data.points_log || [];

  // Group leads by phase
  const futureLeads = leads.filter((l) => l.status === "new" || !l.status);
  const currentLeads = leads.filter((l) => l.status === "in_progress" || l.status === "paid");
  const pastLeads = leads.filter((l) => l.status === "delivered" || l.status === "cancelled");

  return (
    <main className="min-h-screen bg-[#FAF7F2] text-stone-900" data-testid="admin-client-detail-root">
      <Nav />

      <section className="pt-32 pb-10 lg:pt-40 lg:pb-12 px-6 lg:px-12 bg-bubbly relative overflow-hidden">
        <div className="blob blob-teal w-[420px] h-[420px] -top-32 -left-32" />
        <div className="blob blob-peach w-[320px] h-[320px] top-1/3 -right-24" />
        <div className="relative mx-auto max-w-7xl">
          <Link
            to="/admin/clients"
            data-testid="admin-client-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-stone-900 mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> All clients
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <h1
                className="font-display text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.02]"
                data-testid="client-name"
              >
                {u.name || u.email}
              </h1>
              <p
                className="mt-3 text-stone-600 text-lg break-all"
                data-testid="client-email"
              >
                {u.email}
              </p>
              {u.created_at && (
                <p className="mt-1 text-stone-500 text-sm inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Joined {new Date(u.created_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              )}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setPwOpen(true)}
                  data-testid="client-reset-password-btn"
                  className="inline-flex items-center gap-2 bg-white border border-stone-200 text-stone-800 px-4 py-2 rounded-full text-xs font-semibold hover:border-teal-700 hover:text-teal-700 transition-colors"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Reset client password
                </button>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="client-points">
                <div className="text-[11px] uppercase tracking-wider opacity-70 font-semibold inline-flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5" /> Points
                </div>
                <div className="font-display text-3xl font-semibold mt-1 text-amber-900">
                  {u.points}
                </div>
                <div className="text-[11px] text-amber-800 mt-0.5">
                  Lifetime {u.lifetime_points} · {500 - u.points} to next free
                </div>
              </div>
              <div
                className={`border rounded-2xl p-4 ${
                  u.free_credits > 0
                    ? "bg-rose-50 border-rose-300"
                    : "bg-white border-stone-200"
                }`}
                data-testid="client-credits"
              >
                <div className="text-[11px] uppercase tracking-wider opacity-70 font-semibold inline-flex items-center gap-1.5">
                  <Gift className="h-3.5 w-3.5" /> Free credits
                </div>
                <div className="font-display text-3xl font-semibold mt-1">
                  {u.free_credits}
                </div>
                <div className="text-[11px] mt-0.5 opacity-70">
                  {u.free_credits_used} redeemed
                </div>
                {u.free_credits > 0 && (
                  <button
                    type="button"
                    onClick={redeemCredit}
                    data-testid="client-redeem-btn"
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-stone-900 text-white px-3 py-1.5 rounded-full text-[11px] font-semibold hover:bg-teal-700 transition-colors"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Redeem 1
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 pb-10">
        <div className="mx-auto max-w-7xl">
          <UploadZone
            onPick={() => fileInputRef.current?.click()}
            onFiles={handleFiles}
            uploading={uploading}
            noteText={noteText}
            setNoteText={setNoteText}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="client-file-input"
          />

          {deliverables.length > 0 && (
            <div className="mt-8" data-testid="client-deliverables-list">
              <h3 className="font-display text-xl font-semibold mb-4">
                Delivered floor plans ({deliverables.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {deliverables.map((d) => (
                  <div
                    key={d.id}
                    className="bg-white border border-stone-200 rounded-2xl p-4"
                    data-testid={`client-deliverable-${d.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="inline-flex h-10 w-10 rounded-xl bg-teal-100 items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-teal-700" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-stone-900 truncate" title={d.filename}>
                          {d.filename}
                        </div>
                        {d.note && (
                          <div className="text-xs text-stone-600 mt-1 line-clamp-2">
                            {d.note}
                          </div>
                        )}
                        <div className="text-[11px] text-stone-500 mt-1.5">
                          {new Date(d.uploaded_at).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => downloadFile(d.id, d.filename)}
                        data-testid={`client-deliverable-download-${d.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-stone-900 text-white px-3 py-1.5 rounded-full text-[11px] font-semibold hover:bg-teal-700 transition-colors"
                      >
                        <Download className="h-3 w-3" /> Download
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteDeliverable(d.id)}
                        data-testid={`client-deliverable-delete-${d.id}`}
                        className="inline-flex items-center justify-center bg-white border border-rose-200 text-rose-700 px-3 py-1.5 rounded-full text-[11px] font-semibold hover:bg-rose-50 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="pb-24 lg:pb-32 px-6 lg:px-12">
        <div className="mx-auto max-w-7xl space-y-10">
          <OrdersGroup
            title="Current"
            subtitle="In progress or paid, awaiting delivery"
            leads={currentLeads}
            onUpdateStatus={updateLeadStatus}
            testId="orders-current"
          />
          <OrdersGroup
            title="Future"
            subtitle="New requests not yet started"
            leads={futureLeads}
            onUpdateStatus={updateLeadStatus}
            testId="orders-future"
          />
          <OrdersGroup
            title="Past"
            subtitle="Delivered or cancelled"
            leads={pastLeads}
            onUpdateStatus={updateLeadStatus}
            testId="orders-past"
          />

          {pointsLog.length > 0 && (
            <div data-testid="points-log">
              <h3 className="font-display text-xl font-semibold mb-3">Points history</h3>
              <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
                {pointsLog.map((entry) => (
                  <div key={entry.id} className="px-5 py-3 flex items-center justify-between text-sm">
                    <div>
                      <span className="font-semibold text-emerald-700">+{entry.amount} pts</span>
                      {entry.credits_created > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                          <Gift className="h-3 w-3" /> +{entry.credits_created} credit
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500">
                      Balance {entry.balance_after} ·{" "}
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <PasswordModal
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        mode="admin"
        clientId={u.id}
        clientLabel={u.name || u.email}
      />
    </main>
  );
}

function UploadZone({ onPick, onFiles, uploading, noteText, setNoteText }) {
  const [dragging, setDragging] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
  };
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      data-testid="client-upload-zone"
      className={`border-2 border-dashed rounded-[2rem] p-8 transition-colors ${
        dragging ? "border-teal-500 bg-teal-50" : "border-stone-300 bg-white"
      }`}
    >
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <div className="inline-flex h-14 w-14 rounded-2xl bg-teal-100 items-center justify-center flex-shrink-0">
          <UploadCloud className="h-7 w-7 text-teal-700" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h3 className="font-display text-xl font-semibold">
            Upload finished floor plans for this client
          </h3>
          <p className="text-stone-600 text-sm mt-1">
            Drop image or PDF files here, or click to choose. The client will see
            them on their account page.
          </p>
        </div>
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          data-testid="client-upload-btn"
          className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-60"
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" /> Choose files
            </>
          )}
        </button>
      </div>
      <input
        type="text"
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        placeholder="Optional note for the client (e.g. 'Final 2D plan v2')"
        data-testid="client-upload-note"
        className="mt-4 w-full bg-stone-50 border border-stone-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-teal-700"
      />
    </div>
  );
}

function OrdersGroup({ title, subtitle, leads, onUpdateStatus, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-end justify-between mb-3">
        <div>
          <h3 className="font-display text-xl font-semibold">{title}</h3>
          <p className="text-xs text-stone-500">{subtitle}</p>
        </div>
        <div className="text-xs font-semibold text-stone-500">
          {leads.length} {leads.length === 1 ? "order" : "orders"}
        </div>
      </div>
      {leads.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center text-sm text-stone-500">
          Nothing here yet.
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-[2rem] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">When</th>
                  <th className="text-left px-5 py-3 font-semibold">Service</th>
                  <th className="text-left px-5 py-3 font-semibold">Property</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} className="border-t border-stone-100" data-testid={`order-row-${lead.id}`}>
                    <td className="px-5 py-3 text-stone-700 whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-semibold">{lead.service_type}</div>
                      {lead.style && (
                        <div className="text-xs text-stone-500">Style: {lead.style}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-stone-600">
                      {lead.property_address || "—"}
                    </td>
                    <td className="px-5 py-3">
                      <select
                        value={lead.status || "new"}
                        onChange={(e) => onUpdateStatus(lead.id, e.target.value)}
                        data-testid={`order-status-${lead.id}`}
                        className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border-0 focus:outline-none focus:ring-2 focus:ring-stone-400 cursor-pointer ${STATUS_TINT[lead.status || "new"]}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
